import React, { useMemo, useState } from "react";
import Button from "../../components/UI/Buttons/Button/Button";
import AccessibleDialog from "../../components/UI/Modals/AccessibleDialog/AccessibleDialog";
import {
  PHARMACIST_DAYS,
  PHARMACIST_DAY_LABELS,
  PharmacistDayOfWeek,
  PharmacistWorkingHoursEntry,
} from "../../types/pharmacistProfile.types";
import styles from "./PharmacistProfilePage.module.scss";

// ITAM-2622 (3.9) — seven named rows, "Radim" toggle + a pair of <input
// type="time"> (minute granularity, serialized "HH:mm" — seconds are a backend
// 400). Off days are omitted from the payload entirely.
//
// Validation (decision 22): `from < until` STRICTLY. Equal times and overnight
// intervals are rejected at input because every backend availability path
// treats `from >= until` as UNAVAILABLE (BF-14) — they are dead windows, not
// alternative ones.

export interface DayRow {
  enabled: boolean;
  from: string;
  until: string;
}

export type HoursDraft = Record<PharmacistDayOfWeek, DayRow>;

const EMPTY_ROW: DayRow = { enabled: false, from: "08:00", until: "16:00" };

export const buildHoursDraft = (
  entries: PharmacistWorkingHoursEntry[] | null | undefined
): HoursDraft => {
  const draft = {} as HoursDraft;
  PHARMACIST_DAYS.forEach((day) => {
    draft[day] = { ...EMPTY_ROW };
  });
  (entries ?? []).forEach((entry) => {
    if (!PHARMACIST_DAYS.includes(entry.dayOfWeek)) {
      return;
    }
    draft[entry.dayOfWeek] = {
      enabled: true,
      // Tolerate an "HH:mm:ss" the backend might yet return; the input needs
      // exactly "HH:mm".
      from: (entry.availableFrom ?? "").slice(0, 5),
      until: (entry.availableUntil ?? "").slice(0, 5),
    };
  });
  return draft;
};

export const hoursDraftToEntries = (
  draft: HoursDraft
): PharmacistWorkingHoursEntry[] =>
  PHARMACIST_DAYS.filter((day) => draft[day].enabled).map((day) => ({
    dayOfWeek: day,
    availableFrom: draft[day].from,
    availableUntil: draft[day].until,
  }));

/**
 * Accepts "HH:mm" and the "HH:mm:ss" the backend might yet return, and nothing
 * else. Ordering has to be decided on parsed minutes rather than on the strings:
 * a lexicographic comparison happily reports "25:00" < "26:00" as a usable
 * window, so any non-empty junk in the stored schedule would enable activation.
 * Seconds are parsed but not compared — a sub-minute window is a dead window,
 * and the editor works in whole minutes.
 */
const minutesOfDay = (raw: string | null | undefined): number | null => {
  const match = /^([01]\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?$/.exec(
    (raw ?? "").trim()
  );
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
};

/**
 * Decision 21 gates activation on at least one *valid* interval — deliberately
 * not on a non-empty schedule. BF-14: every backend availability path carries
 * `WHEN NOT (available_from < available_until) THEN 'UNAVAILABLE'`, so an equal
 * or overnight row is a dead window, and enrolling on one produces a
 * pharmacist who is never free and cannot see why. The editor rejects such rows
 * at input, but the gate reads SERVER data, which can still hold one from
 * before this ticket or from another client.
 */
export const hasUsableSchedule = (
  entries: PharmacistWorkingHoursEntry[] | null | undefined
): boolean =>
  (entries ?? []).some((entry) => {
    const from = minutesOfDay(entry.availableFrom);
    const until = minutesOfDay(entry.availableUntil);
    return from != null && until != null && from < until;
  });

export const OVERNIGHT_ERROR =
  "Prekonoćne smene nisu podržane; unesite vreme u okviru istog dana.";

export const validateHoursDraft = (
  draft: HoursDraft
): Partial<Record<PharmacistDayOfWeek, string>> => {
  const errors: Partial<Record<PharmacistDayOfWeek, string>> = {};
  PHARMACIST_DAYS.forEach((day) => {
    const row = draft[day];
    if (!row.enabled) {
      return;
    }
    if (!row.from || !row.until) {
      errors[day] = "Unesite vreme od i vreme do.";
      return;
    }
    if (row.from >= row.until) {
      errors[day] = OVERNIGHT_ERROR;
    }
  });
  return errors;
};

interface WorkingHoursEditorProps {
  draft: HoursDraft;
  onChange: (next: HoursDraft) => void;
  onSave: (entries: PharmacistWorkingHoursEntry[]) => void;
  saving: boolean;
  saveError: string | null;
  savedNotice: boolean;
}

const WorkingHoursEditor: React.FC<WorkingHoursEditorProps> = ({
  draft,
  onChange,
  onSave,
  saving,
  saveError,
  savedNotice,
}) => {
  const [errors, setErrors] = useState<
    Partial<Record<PharmacistDayOfWeek, string>>
  >({});
  const [confirmingClear, setConfirmingClear] = useState(false);

  const entries = useMemo(() => hoursDraftToEntries(draft), [draft]);

  const update = (day: PharmacistDayOfWeek, patch: Partial<DayRow>) => {
    onChange({ ...draft, [day]: { ...draft[day], ...patch } });
  };

  const submit = () => {
    const nextErrors = validateHoursDraft(draft);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }
    // Clearing the whole schedule makes the pharmacist permanently
    // unavailable, so it is never a silent side effect of pressing "save".
    if (entries.length === 0) {
      setConfirmingClear(true);
      return;
    }
    onSave(entries);
  };

  return (
    <section className={styles.card} aria-labelledby="pharmacist-hours-title">
      <h2 id="pharmacist-hours-title" className={styles.cardTitle}>
        Radno vreme
      </h2>
      <p className={styles.hint}>
        Dostupnost se menja prema rasporedu (do 5 minuta kašnjenja).
      </p>

      <ul className={styles.dayList}>
        {PHARMACIST_DAYS.map((day) => {
          const row = draft[day];
          const error = errors[day];
          return (
            <li key={day} className={styles.dayRow}>
              <label className={styles.dayToggle}>
                <input
                  type="checkbox"
                  checked={row.enabled}
                  disabled={saving}
                  onChange={(e) =>
                    update(day, { enabled: e.target.checked })
                  }
                />
                <span className={styles.dayName}>
                  {PHARMACIST_DAY_LABELS[day]}
                </span>
              </label>
              <div className={styles.dayTimes}>
                <label className={styles.timeField}>
                  <span className={styles.timeLabel}>Od</span>
                  <input
                    type="time"
                    value={row.from}
                    disabled={!row.enabled || saving}
                    aria-label={`${PHARMACIST_DAY_LABELS[day]} — vreme od`}
                    onChange={(e) => update(day, { from: e.target.value })}
                  />
                </label>
                <label className={styles.timeField}>
                  <span className={styles.timeLabel}>Do</span>
                  <input
                    type="time"
                    value={row.until}
                    disabled={!row.enabled || saving}
                    aria-label={`${PHARMACIST_DAY_LABELS[day]} — vreme do`}
                    onChange={(e) => update(day, { until: e.target.value })}
                  />
                </label>
              </div>
              {error && (
                <p className={styles.fieldError} role="alert">
                  {error}
                </p>
              )}
            </li>
          );
        })}
      </ul>

      {saveError && (
        <p className={styles.error} role="alert">
          {saveError}
        </p>
      )}
      {savedNotice && !saveError && (
        <p className={styles.notice} role="status">
          Radno vreme je sačuvano. Dostupnost se usklađuje sa rasporedom (do 5
          minuta kašnjenja).
        </p>
      )}

      <Button buttonType="blue" onClick={submit} disabled={saving}>
        {saving ? "Čuvanje..." : "Sačuvaj radno vreme"}
      </Button>

      <AccessibleDialog
        isOpen={confirmingClear}
        onRequestClose={() => setConfirmingClear(false)}
        titleId="pharmacist-hours-clear-title"
        maxWidth={480}
      >
        <div className={styles.dialogBody}>
          <h3 id="pharmacist-hours-clear-title" className={styles.dialogTitle}>
            Uklanjate celo radno vreme
          </h3>
          <p>Uklanjate celo radno vreme — bićete nedostupni.</p>
          <div className={styles.dialogActions}>
            <Button
              buttonType="white"
              onClick={() => setConfirmingClear(false)}
            >
              Odustani
            </Button>
            <Button
              buttonType="red"
              onClick={() => {
                setConfirmingClear(false);
                onSave([]);
              }}
            >
              Ukloni radno vreme
            </Button>
          </div>
        </div>
      </AccessibleDialog>
    </section>
  );
};

export default WorkingHoursEditor;
